import React, { useState, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useIdentity } from '../contexts/IdentityContext';
import { useAuth } from '../contexts/AuthContext';
import { gemsApi, preachingApi } from '../lib/api';
import GemCard from '../components/GemCard';
import { Search as SearchIcon, Gem, ClipboardList, Loader2, Heart, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const CATEGORIES = [
  { id: 'presentoir', icon: '📋' }, { id: 'porte_a_porte', icon: '🚪' },
  { id: 'rue', icon: '🚶' }, { id: 'telephone', icon: '📞' },
  { id: 'courrier', icon: '✉️' }, { id: 'informel', icon: '💬' },
];

export default function SearchPage() {
  const { t } = useLanguage();
  const { anonId } = useIdentity();
  const { user } = useAuth();
  
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState('gems');
  const [gemResults, setGemResults] = useState([]);
  const [preachingResults, setPreachingResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = useCallback(async () => {
    if (!query.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      const [gemsRes, preachRes] = await Promise.all([
        gemsApi.getGems(null, 1, 50, query),
        preachingApi.getReports(null, 1, 50, query),
      ]);
      setGemResults(gemsRes.data.gems || []);
      setPreachingResults(preachRes.data.reports || []);
    } catch {
      toast.error(t('search_failed') || 'Search failed');
    } finally {
      setLoading(false);
    }
  }, [query, t]);

  const handleLike = async (gemId) => {
    try {
      const res = await gemsApi.toggleLike(gemId, anonId);
      setGemResults(prev => prev.map(g => {
        if (g.id === gemId) {
          const liked = res.data.action === 'liked';
          return { ...g, likes_count: res.data.likes_count, liked_by: liked ? [...(g.liked_by||[]), anonId] : (g.liked_by||[]).filter(id => id !== anonId) };
        }
        return g;
      }));
    } catch { toast.error(t('error') || 'Failed'); }
  };

  const handleShare = async (gem) => {
    const text = `${gem.text}${gem.verse_ref ? '\n\n' + gem.verse_ref : ''}`;
    try {
      if (navigator.share) await navigator.share({ title: 'Spiritual Gem', text });
      else { await navigator.clipboard.writeText(text); toast.success(t('copied') || 'Copied'); }
    } catch {}
  };

  const handlePreachingLike = async (id) => {
    try {
      const res = await preachingApi.toggleLike(id, anonId);
      setPreachingResults(prev => prev.map(r => r.id === id ? { ...r, likes_count: res.data.likes_count, likes: res.data.action === 'liked' ? [...(r.likes||[]), anonId] : (r.likes||[]).filter(i => i !== anonId) } : r));
    } catch {}
  };

  const getCat = (id) => CATEGORIES.find(c => c.id === id) || { icon: '📋' };
  const totalResults = gemResults.length + preachingResults.length;

  return (
    <div className="px-4 pt-4 pb-24">
      <h1 className="text-xl font-[var(--font-heading)] tracking-[-0.01em] text-foreground flex items-center gap-2 mb-4">
        <SearchIcon className="w-5 h-5 text-primary" />
        {t('search')}
      </h1>

      {/* Search bar */}
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder={t('search_all_placeholder') || 'Search gems & preaching reports...'}
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            data-testid="search-input" />
        </div>
        <button onClick={handleSearch} disabled={loading || !query.trim()}
          className="px-4 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <SearchIcon className="w-4 h-4" />}
        </button>
      </div>

      {/* Tabs */}
      {searched && (
        <div className="flex gap-1 mb-4 border-b border-border">
          <button onClick={() => setActiveTab('gems')}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium relative ${activeTab === 'gems' ? 'text-primary' : 'text-muted-foreground'}`}>
            <Gem className="w-3.5 h-3.5" />{t('app_name')} ({gemResults.length})
            {activeTab === 'gems' && <motion.div layoutId="searchTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
          </button>
          <button onClick={() => setActiveTab('preaching')}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium relative ${activeTab === 'preaching' ? 'text-primary' : 'text-muted-foreground'}`}>
            <ClipboardList className="w-3.5 h-3.5" />{t('preaching')} ({preachingResults.length})
            {activeTab === 'preaching' && <motion.div layoutId="searchTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
          </button>
        </div>
      )}

      {/* Results */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>
      ) : searched && totalResults === 0 ? (
        <div className="text-center py-16">
          <SearchIcon className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">{t('no_results') || 'No results found'}</p>
        </div>
      ) : activeTab === 'gems' ? (
        <div className="space-y-4">
          {gemResults.map(gem => (
            <GemCard key={gem.id} gem={gem} anonId={anonId} userId={user?.id} userName={user?.pseudonym}
              onLike={handleLike} onShare={handleShare} />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {preachingResults.map(report => {
            const cat = getCat(report.category);
            const liked = (report.likes || []).includes(anonId);
            return (
              <motion.div key={report.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="rounded-[var(--radius)] border border-border bg-card p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                    {report.author_photo ? <img src={report.author_photo} alt="" className="w-full h-full object-cover" /> :
                      <span className="text-xs font-medium text-primary">{(report.author_name || 'A')[0]}</span>}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{report.author_name}</p>
                    <p className="text-[10px] text-muted-foreground">{report.date}</p>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] bg-accent text-muted-foreground">
                    {cat.icon} {t(`cat_${report.category}`) || report.category}
                  </span>
                </div>
                {report.notes && <p className="text-sm text-foreground/90 mb-2">{report.notes}</p>}
                <button onClick={() => handlePreachingLike(report.id)}
                  className={`flex items-center gap-1 text-xs ${liked ? 'text-red-500' : 'text-muted-foreground'}`}>
                  <Heart className={`w-3.5 h-3.5 ${liked ? 'fill-current' : ''}`} />{report.likes_count || 0}
                </button>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
